/**
 * AdmissionLayout — shared chrome for every Admission Analytics screen.
 *
 * Mirrors the Result module's `ResultLayout`: it renders the app `Header`, the
 * route-driven `Breadcrumb`, a purple sub-navigation bar that switches between
 * the module's screens, then the active screen via `<Outlet/>`, then `Footer`.
 *
 * The lazily-loaded child screens are wrapped here in a `Suspense` + error
 * boundary so the shared chrome (header + sub-nav) stays visible while a screen
 * loads and an `ErrorState` renders in the content area if one fails.
 */

import React, { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Map } from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import { KVSLoader } from '../../../components/loaders';
import ErrorState from '../components/ErrorState';

// RTE / Income Group / Distance were merged into the Geographic screen as
// views over one shared filter — see GeographicAnalysis's header.
const NAV_ITEMS = [
  { to: '/admission/geographic', label: 'Geographic', icon: Map, end: false },
];

/** Renders `ErrorState` if a lazily-loaded screen fails to load or throws. */
class ScreenErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  handleRetry = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-[1600px] mx-auto px-4 py-10">
          <ErrorState message="This admission screen could not be loaded." onRetry={this.handleRetry} />
        </div>
      );
    }
    return this.props.children;
  }
}

const ScreenLoader: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <KVSLoader />
  </div>
);

const AdmissionLayout: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />

      {/* Sub-navigation across the admission screens */}
      <div className="w-full bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  'flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ' +
                  (isActive
                    ? 'text-[#6B1786] border-[#6B1786]'
                    : 'text-gray-500 border-transparent hover:text-gray-800 hover:bg-gray-50')
                }
              >
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-1 w-full">
        <ScreenErrorBoundary>
          <Suspense fallback={<ScreenLoader />}>
            <Outlet />
          </Suspense>
        </ScreenErrorBoundary>
      </main>

      <Footer />
    </div>
  );
};

export default AdmissionLayout;
